/**
 * Wall clocks, in the **tenant's** timezone.
 *
 * The rule this file exists to enforce, in one sentence: *a guest books the
 * spa's two o'clock, not their laptop's.* Everything on the wire is an
 * `IsoDateTime` with an explicit offset (agreement C3), and every one of those
 * instants is rendered through `site.timezone` from `/api/site` — never through
 * the browser's. A guest in New York booking a treatment in St Thomas must see
 * `2:00 pm`, the time they will be standing at the door, and a guest in
 * Frenchtown must see the same string for the same slot.
 *
 * This is not hypothetical for this product: the USVI does not observe daylight
 * saving and most of its guests come from places that do, so the offset between
 * a visitor's device and the shop moves twice a year. `Intl.DateTimeFormat` with
 * an explicit `timeZone` is the whole fix, and doing it in one module is what
 * keeps a component from reaching for `toLocaleTimeString()` on a tired
 * afternoon.
 *
 * Dates on the wire are already tenant-local calendar days (`YYYY-MM-DD`,
 * agreement C4), so day arithmetic here is pure string work and never touches a
 * `Date` — a `new Date('2026-09-03')` is midnight *UTC*, which in St Thomas is
 * the evening of the 2nd, and that off-by-one is the classic way a booking site
 * offers yesterday.
 *
 * The locale is `en-US` today. `docs/i18n-design.md` is landing the locale rails
 * (`clients.locale`, localized JSONB) in parallel; when they arrive the tenant's
 * locale replaces the constant below and nothing else in this app changes,
 * because nothing else in this app formats a time.
 */

const LOCALE = 'en-US';

export interface Clock {
  timezone: string;
  /** `2:00 pm` */
  time(iso: string): string;
  /** `Thursday, September 3` */
  dayLong(iso: string): string;
  /** `Thu 3 Sep` — for a row of day chips. */
  dayShort(isoDay: string): string;
  /** `2:00 – 3:00 pm, Thursday, September 3` */
  slot(startIso: string, endIso: string): string;
  /** The tenant's *today*, as `YYYY-MM-DD`. */
  today(now?: Date): string;
  /** The calendar day an instant falls on, in the tenant's zone. */
  dayOf(iso: string): string;
}

export function clockFor(timezone: string): Clock {
  const zone = safeZone(timezone);

  const time = new Intl.DateTimeFormat(LOCALE, {
    timeZone: zone,
    hour: 'numeric',
    minute: '2-digit',
  });
  const dayLong = new Intl.DateTimeFormat(LOCALE, {
    timeZone: zone,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
  const dayShort = new Intl.DateTimeFormat(LOCALE, {
    timeZone: zone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  // `en-CA` is the shortest honest way to get `YYYY-MM-DD` out of `Intl`; the
  // alternative is `formatToParts` and three lookups. The locale here is a date
  // *format*, not a language choice, which is why it does not move with i18n.
  const isoDay = new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  return {
    timezone: zone,
    time: (iso) => time.format(new Date(iso)),
    dayLong: (iso) => dayLong.format(new Date(iso)),
    dayShort: (day) => dayShort.format(noonUtcOf(day)),
    slot: (startIso, endIso) =>
      `${time.format(new Date(startIso))} – ${time.format(new Date(endIso))}, ${dayLong.format(
        new Date(startIso),
      )}`,
    today: (now = new Date()) => isoDay.format(now),
    dayOf: (iso) => isoDay.format(new Date(iso)),
  };
}

/**
 * A tenant row could name a zone this runtime does not have — an old browser, or
 * a typo that reached `tenants.timezone`. `Intl` throws on an unknown zone, and
 * a booking page that white-screens because of a spelling is worse than one that
 * quietly falls back to UTC and shows times an hour or four out. Neither is
 * good; the second is recoverable by a human reading the page.
 */
function safeZone(timezone: string): string {
  try {
    new Intl.DateTimeFormat(LOCALE, { timeZone: timezone });
    return timezone;
  } catch {
    console.warn(`[time] unknown timezone "${timezone}"; falling back to UTC.`);
    return 'UTC';
  }
}

/**
 * `2026-09-03` → the instant at 12:00 UTC on that date.
 *
 * Noon, not midnight: the label only ever needs the weekday and the day number,
 * and noon UTC lands on the same calendar day in every zone from UTC-11 to
 * UTC+11. Midnight would land on the previous day everywhere west of Greenwich.
 */
function noonUtcOf(isoDay: string): Date {
  return new Date(`${isoDay}T12:00:00.000Z`);
}

/* ------------------------------------------------------------------ */
/* Wall clocks with no instant behind them — opening hours             */
/* ------------------------------------------------------------------ */

/**
 * The two formatters opening hours need, and the thing that makes them
 * different from everything above: a `DayHours` window is a **wall clock and a
 * weekday**, not a moment. `11:30` on a Tuesday is not an instant, so there is
 * no timezone to convert through — the tenant's zone is already the zone these
 * strings are written in, and `/api/site` says so on the page.
 *
 * So both formatters run in UTC against a reference week, which is a way of
 * saying "format these digits, do not move them". 2024-01-01 is a Monday, which
 * is also `DayHours.weekday === 0` — the domain is Monday-first.
 */
const REFERENCE_MONDAY = Date.UTC(2024, 0, 1);

const weekdayLong = new Intl.DateTimeFormat(LOCALE, { timeZone: 'UTC', weekday: 'long' });
const wallClock = new Intl.DateTimeFormat(LOCALE, {
  timeZone: 'UTC',
  hour: 'numeric',
  minute: '2-digit',
});

/** `0` → `Monday`, `6` → `Sunday`. Monday-first, as `DayHours.weekday` is. */
export function weekdayName(weekday: number): string {
  const index = ((Math.trunc(weekday) % 7) + 7) % 7;
  return weekdayLong.format(new Date(REFERENCE_MONDAY + index * 86_400_000));
}

/**
 * `08:00` → `8:00 AM`. Anything that is not `HH:MM` comes back unchanged — a
 * hand-edited `tenant_config.hours` is data this bundle displays, not data it
 * gets to refuse.
 *
 * The narrow no-break space ICU 72 started putting before `AM` is folded back
 * to an ordinary one. Not cosmetics: it is invisible, it varies with the ICU
 * the runtime was built against, and a string that differs between two Node
 * builds is a test that fails on somebody else's laptop for no reason.
 */
export function wallTime(hhmm: string): string {
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (match === null) return hhmm;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return hhmm;
  return wallClock
    .format(new Date(REFERENCE_MONDAY + (hour * 60 + minute) * 60_000))
    .replace(/\s+/g, ' ');
}

/* ------------------------------------------------------------------ */
/* Calendar-day arithmetic — strings in, strings out                   */
/* ------------------------------------------------------------------ */

export function addDays(isoDay: string, days: number): string {
  const [y, m, d] = isoDay.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

export function dayRange(from: string, count: number): string[] {
  return Array.from({ length: count }, (_, i) => addDays(from, i));
}

/** Positive when `b` is later. Whole days, no clock involved. */
export function dayDistance(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

/** `12000` → `$120`, `12550` → `$125.50`. Trailing `.00` is noise on a menu. */
export function money(cents: number, currency: string): string {
  const whole = cents % 100 === 0;
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency: currencyOr(currency),
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(cents / 100);
}

function currencyOr(code: string): string {
  return /^[A-Z]{3}$/.test(code) ? code : 'USD';
}

/** `60` → `1 hr`, `90` → `1 hr 30 min`, `45` → `45 min`. */
export function duration(minutes: number): string {
  if (minutes < 60) return `${String(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const h = `${String(hours)} hr`;
  return rest === 0 ? h : `${h} ${String(rest)} min`;
}
